// Deep import (not the `@ping/ui` barrel): the barrel mounts the toast host,
// whose prebuilt `.js` contains raw JSX that Vite can't transform under vitest —
// pulling it here made every persistence unit test fail import-analysis. This
// module is pure byte helpers with no side effects. (See the vitest deep-import
// gotcha in the team memory.)
import { hexToBytes } from '@ping/ui/src/conversation/mlsBytes';
import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';
import { leaveConversationOnServer } from '@ping/ui/src/network/conversations';
import { isConversationVisible } from '@ping/ui/src/sync/conversationVisibility';

import { log } from '@core/log/log';
import { deleteSyncCursor } from '@core/storage/idb';
import { sendConvState } from '@features/tasks/deviceGroupTaskSync';

import type { Conversation } from './db';
import { db } from './db';

/**
 * Mirror a per-conversation personal-state change to the user's OTHER devices
 * over the DeviceGroup (multi-device conversation-state sync). Best-effort and
 * fire-and-forget — the local Dexie write above is authoritative; a sibling that
 * is offline reconciles on its next sync. Reads the live SDK client lazily so
 * these persistence helpers stay usable before/without a session.
 */
function broadcastConvState(id: string, patch: Record<string, unknown>): void {
  void sendConvState(
    (useSessionStore.getState().status as { client?: unknown }).client ?? null,
    id,
    patch,
  );
}

export async function upsert(conversation: Conversation): Promise<void> {
  await db.conversations.put(conversation);
}

export async function get(id: string): Promise<Conversation | undefined> {
  return db.conversations.get(id);
}

export async function list(options?: { includeArchived?: boolean }): Promise<Conversation[]> {
  const { includeArchived = false } = options ?? {};

  const allConversations = await db.conversations.toArray();

  // Route through the SINGLE visibility authority (`isConversationVisible` →
  // `conversationHiddenReason`) instead of an ad-hoc `deletedAt`-only check, so
  // this path can never diverge from the list adapter: soft-deleted, server-
  // orphaned, device-group, and per-call groups are all excluded by the same
  // rule the chat list uses. (A LEFT group is intentionally kept visible +
  // read-only for re-invite resume — see `conversationVisibility`.)
  // `archivedAt` is a SEPARATE axis (archived
  // chats live in their own section, they are not "hidden"), so it stays a
  // distinct, opt-in filter here rather than folding into the gate.
  const filtered = (
    includeArchived
      ? allConversations
      : allConversations.filter((conv) => conv.archivedAt === undefined)
  ).filter(isConversationVisible);

  return filtered.sort((a, b) => {
    const aTime = a.lastMessageAt ?? 0;
    const bTime = b.lastMessageAt ?? 0;
    return bTime - aTime;
  });
}

export async function archive(id: string): Promise<void> {
  const archivedAt = Date.now();
  await db.conversations.update(id, { archivedAt });
  broadcastConvState(id, { archivedAt });
}

export async function unarchive(id: string): Promise<void> {
  const conversation = await db.conversations.get(id);
  if (!conversation) return;
  // Strip `archivedAt` from the record by destructuring it into a
  // throwaway name and spreading the rest. The leading underscore
  // satisfies the project's `@typescript-eslint/no-unused-vars`
  // `varsIgnorePattern: ^_` config.
  const { archivedAt: _archivedAt, ...rest } = conversation;
  await db.conversations.put({ ...rest, id });
  // Siblings clear their archive marker on `archivedAt: 0` (see applyConvStatePatch).
  broadcastConvState(id, { archivedAt: 0 });
}

export async function pin(id: string): Promise<void> {
  const pinnedAt = Date.now();
  await db.conversations.update(id, { pinnedAt });
  broadcastConvState(id, { pinnedAt });
}

export async function unpin(id: string): Promise<void> {
  const conversation = await db.conversations.get(id);
  if (!conversation) return;
  // Strip `pinnedAt` entirely (Dexie ignores `undefined` patch keys, so we
  // rebuild the row without it) — mirrors `unarchive()`.
  const { pinnedAt: _pinnedAt, ...rest } = conversation;
  await db.conversations.put({ ...rest, id });
  // Siblings clear their pin on `pinnedAt: 0` (see applyConvStatePatch).
  broadcastConvState(id, { pinnedAt: 0 });
}

/**
 * SOFT-delete a conversation (chat-list "Delete"): wipe the local message
 * history + hide the row, but KEEP the identity (name, avatar, kind, roster,
 * peerAccountId). A hard delete would lose all of that — and since the MLS group
 * isn't deleted, a peer's next message would resume the chat as a nameless
 * "New chat" (a group even degrades to a 1:1). With the identity preserved, the
 * incoming path just un-hides the row (`deletedAt = 0`) and everything is
 * correct again. Mirrors WhatsApp/Signal "delete chat" semantics.
 */
export async function deleteConversation(id: string): Promise<void> {
  await db.transaction('rw', db.messages, db.conversations, async () => {
    await db.messages.where('conversationId').equals(id).delete();
    await db.conversations
      .where('id')
      .equals(id)
      .modify((c) => {
        c.deletedAt = Date.now();
        delete c.lastMessagePreview;
        delete c.lastMessageAt;
        delete c.lastMessageSenderAccountId;
        (c as { unreadCount?: number }).unreadCount = 0;
      });
  });
  // Mirror the "delete chat" to the user's other devices: a `deletedAt > 0`
  // patch tells siblings to wipe their local history + hide the row too.
  broadcastConvState(id, { deletedAt: Date.now() });
}

/**
 * Mark a group as LEFT — non-destructively. Unlike {@link deleteConversation}
 * this keeps the conversation row AND all message history (the user may want to
 * keep old conversations); it only stamps `leftAt` so the composer goes
 * read-only and the roster no longer counts us. It must NOT touch `db.messages`,
 * must NOT set `deletedAt`, and must NOT clear the preview/lastMessage fields.
 * Broadcasts `leftAt` to sibling devices so they mark the group read-only too
 * (WITHOUT the destructive history wipe that a `deletedAt` patch triggers).
 */
/**
 * LOCAL-ONLY non-destructive leave mark: set `leftAt` on the Dexie row so the
 * conversation goes read-only IMMEDIATELY — the composer's `hasLeftGroup` gate is
 * driven by this via the `useConversations` liveQuery. It does NOT mirror to
 * sibling devices and does NOT tell the server, so it is safe to call BEFORE the
 * "X left" broadcast is sent while still a member (the server-authoritative leave
 * in {@link markConversationLeft} would mark us departed and could reject that
 * still-a-member send). Returns the applied `leftAt`. Use this for the optimistic
 * UI flip, then call {@link markConversationLeft} once the broadcast has gone out.
 */
export async function markConversationLeftLocal(id: string): Promise<number> {
  const leftAt = Date.now();
  await db.conversations
    .where('id')
    .equals(id)
    .modify((c) => {
      (c as { leftAt?: number }).leftAt = leftAt;
    });
  return leftAt;
}

/**
 * Record the MLS epoch we left at (captured from the leave broadcast's send). The
 * re-invite detector (`persistIfNew`) clears `leftAt` only on a live update at an
 * epoch STRICTLY GREATER than this, so a replayed old Welcome on reconnect can
 * never reactivate the composer — only a genuine re-add (higher epoch) does.
 */
export async function setConversationLeftEpoch(id: string, epoch: number): Promise<void> {
  if (!Number.isFinite(epoch)) return;
  await db.conversations
    .where('id')
    .equals(id)
    .modify((c) => {
      (c as { leftAtEpoch?: number }).leftAtEpoch = epoch;
    });
}

export async function markConversationLeft(id: string): Promise<void> {
  const leftAt = await markConversationLeftLocal(id);
  broadcastConvState(id, { leftAt });
  // Server-authoritative leave: mark the membership departed on the backend so
  // this device drops out of its conversation list snapshot AND the account's
  // other devices learn of the departure via an `op=removed` membership delta
  // (docs/architecture/membership-sync.md). Best-effort — the local write above
  // + the device-group `conv_state` mirror are the primary path.
  void leaveConversationOnServer(id).catch(() => undefined);
}

/**
 * Mark a conversation SERVER-ORPHANED (`orphanedAt > 0`) — the single, shared
 * "the server authoritatively denies this conversation" write, used by BOTH the
 * transport's consecutive-404 `onConversationOrphaned` callback AND the
 * members-endpoint `not_member` (403/404) verdict during profile hydration.
 *
 * This is the load-bearing guard for the ghost "New chat": a fresh device (or a
 * device whose SDK holds a stale MLS group the backend never admitted) surfaces
 * a conversation the server rejects; orphaning hides it via the visibility gate
 * (`conversationHiddenReason` → 'orphaned') WITHOUT destroying anything —
 * messages/drafts/`mls.conv.*` KV are preserved, and a genuine revive (server
 * relist in `bootstrapConversationsFromServer`, or a real inbound message) clears
 * `orphanedAt`. Distinct from `deletedAt` (a user delete) so a re-emitted Welcome
 * on resume can never un-hide it (that flapped the ghost every reconnect).
 *
 * Returns `true` if a local row existed (a REAL orphan the user could notice),
 * `false` if the id wasn't in Dexie (expected device-group / never-materialized
 * churn) — the caller uses this only to pick a log level.
 */
export async function markConversationServerOrphaned(
  id: string,
  reason: string,
): Promise<boolean> {
  const key = id.toLowerCase();
  let existed = false;
  try {
    const existingRow = await db.conversations.get(key);
    existed = existingRow != null;
    // NON-DESTRUCTIVE-LEAVE GUARD. A group the local user deliberately LEFT
    // legitimately 404s `fetchSince` and returns `not_member` from `GET /members`
    // (the server dropped our membership on `POST /leave`). That is NOT a ghost —
    // orphaning it would HIDE the row (conversationVisibility hides `orphaned`),
    // drop its cursor, and (if empty) destroy the SDK group, defeating the
    // keep-visible-read-only design. Left rows are intentional terminal state, so
    // never orphan them here — this single choke point covers BOTH the hydration
    // `not_member` verdict and the transport consecutive-404 callback.
    if (((existingRow as { leftAt?: number } | undefined)?.leftAt ?? 0) > 0) {
      log.info({
        event: 'conversation.serverOrphaned.skippedLeft',
        reason,
        conversationIdPrefix: key.slice(0, 8),
      });
      return existed;
    }
    await db.conversations.update(key, { orphanedAt: Date.now() });
    log.info({
      event: 'conversation.serverOrphaned',
      reason,
      existed,
      conversationIdPrefix: key.slice(0, 8),
    });
  } catch (err) {
    log.warn({
      event: 'conversation.serverOrphaned.failed',
      reason,
      message: (err as Error).message,
    });
  }
  // Drop the delivery cursor so a revive re-syncs from a clean slate.
  try {
    await deleteSyncCursor(key);
  } catch {
    /* best-effort */
  }

  // HARD-EVICT the dead group from the SDK's local MLS store — but ONLY for a
  // true ghost (an EMPTY conversation the server never backed). Soft-hiding the
  // Dexie row isn't enough on its own: the SDK keeps the orphan group in
  // `ping-mls-storage`, so every reload's `syncConversations` sweep re-emits it
  // and re-materialises a "New chat" row (and it re-404s forever). Evicting it
  // stops the resurrection at the source. Gated on EMPTY (no messages): a
  // conversation with history is only ever soft-hidden — we never destroy its
  // MLS state (a genuine removal uses `leftAt` and keeps history). Best-effort +
  // feature-detected so it no-ops on an SDK build without the method.
  try {
    const empty = (await db.messages.where('conversationId').equals(key).count()) === 0;
    if (empty) await dropDeadSdkGroup(key);
  } catch {
    /* best-effort — the soft-hide above already removed it from the UI */
  }
  return existed;
}

/**
 * Ask the SDK to delete a conversation's local MLS group state (no network) so a
 * server-unbacked ghost stops rehydrating. Feature-detected: `dropConversationLocal`
 * lands in a newer SDK; on an older build this is a no-op and the row stays
 * soft-hidden (still invisible, just not evicted from `ping-mls-storage`).
 */
async function dropDeadSdkGroup(idHex: string): Promise<void> {
  const status = useSessionStore.getState().status;
  if (status.kind !== 'authenticated' || status.client === null) return;
  const client = status.client as unknown as {
    dropConversationLocal?: (id: Uint8Array) => Promise<void>;
  };
  if (typeof client.dropConversationLocal !== 'function') return;
  let idBytes: Uint8Array;
  try {
    idBytes = hexToBytes(idHex);
  } catch {
    return;
  }
  await client.dropConversationLocal(idBytes);
  log.info({ event: 'conversation.sdkGroupDropped', conversationIdPrefix: idHex.slice(0, 8) });
}

/**
 * HARD-delete a conversation row + its messages (no identity kept, no un-hide
 * on next epoch). Used to purge a per-call MLS group that briefly materialized
 * as a chat row before the call layer registered it — call groups are never
 * chats. Unlike `deleteConversation` (soft), this won't reappear on the next
 * commit because the call-group registry also blocks re-creation.
 */
export async function purgeConversation(id: string): Promise<void> {
  await db.transaction('rw', db.messages, db.conversations, async () => {
    await db.messages.where('conversationId').equals(id).delete();
    await db.conversations.delete(id);
  });
}

export async function mute(id: string): Promise<void> {
  await db.conversations.update(id, { isMuted: true });
  broadcastConvState(id, { isMuted: true });
}

export async function unmute(id: string): Promise<void> {
  await db.conversations.update(id, { isMuted: false });
  broadcastConvState(id, { isMuted: false });
}

/**
 * Per-conversation notification level (Chat Info → Notifications). Keeps the
 * legacy `isMuted` flag in sync so any reader that still checks it stays
 * correct: 'all' = unmuted, 'mentions'/'off' = muted (the gate additionally
 * silences @mentions for 'off').
 */
export async function setNotificationLevel(
  id: string,
  level: 'all' | 'mentions' | 'off',
): Promise<void> {
  await db.conversations.update(id, { notificationLevel: level, isMuted: level !== 'all' });
  broadcastConvState(id, { notificationLevel: level, isMuted: level !== 'all' });
}

/** Per-conversation favorite marker (Chat Info → Profile "Favorite"). */
export async function setFavorite(id: string, favorite: boolean): Promise<void> {
  await db.conversations.update(id, { favorite });
  broadcastConvState(id, { favorite });
}

/** Per-device private nickname/alias (Chat Info → Settings). Local-only.
 *  An empty string clears it. */
export async function setNickname(id: string, nickname: string): Promise<void> {
  // Store the trimmed value (empty string = cleared; readers treat '' as none).
  await db.conversations.update(id, { nickname: nickname.trim() });
}

/** Disappearing-messages TTL (seconds; 0 = off). Mirrors the latest
 *  `com.ping.disappearing_policy` for this conversation. */
export async function setDisappearingTtl(id: string, ttlSeconds: number): Promise<void> {
  await db.conversations.update(id, { disappearingTtlSeconds: ttlSeconds });
}

/** Set a group member's role (owner/admin/member) in the local roster mirror. */
export async function setMemberRole(
  id: string,
  accountId: string,
  role: 'owner' | 'admin' | 'member',
): Promise<void> {
  const conv = await db.conversations.get(id);
  if (conv === undefined) return;
  const next = { ...(conv.memberRoles ?? {}), [accountId]: role };
  await db.conversations.update(id, { memberRoles: next });
}

/** Per-device "verified this peer's safety number" flag. */
export async function setSafetyVerified(id: string, verified: boolean): Promise<void> {
  await db.conversations.update(id, { safetyVerified: verified });
}

/** 1:1 peer's Persona KYC identity-verified status (from their profile). */
export async function setConversationIdentityVerified(
  id: string,
  identityVerified: boolean,
): Promise<void> {
  await db.conversations.update(id, { identityVerified });
}

/**
 * Merge resolved member profiles (accountId → displayName / avatar URL) into a
 * group's local roster mirror (`memberDisplayNames` + `memberAvatarUris`).
 *
 * Guarantees the "never blank" invariant: only NON-EMPTY values are written, so
 * a failed or partial directory fetch can never clobber a previously-good name
 * or avatar with undefined. Returns true if anything changed (so callers can
 * skip a redundant Dexie write / re-render).
 */
export async function mergeMemberProfiles(
  id: string,
  profiles: Record<string, { displayName?: string; avatarUri?: string }>,
): Promise<boolean> {
  const conv = await db.conversations.get(id);
  if (conv === undefined) return false;
  const names = { ...(conv.memberDisplayNames ?? {}) };
  const avatars = { ...(conv.memberAvatarUris ?? {}) };
  let changed = false;
  for (const [accountId, p] of Object.entries(profiles)) {
    if (
      p.displayName !== undefined &&
      p.displayName.length > 0 &&
      names[accountId] !== p.displayName
    ) {
      names[accountId] = p.displayName;
      changed = true;
    }
    if (p.avatarUri !== undefined && p.avatarUri.length > 0 && avatars[accountId] !== p.avatarUri) {
      avatars[accountId] = p.avatarUri;
      changed = true;
    }
  }
  if (!changed) return false;
  await db.conversations.update(id, { memberDisplayNames: names, memberAvatarUris: avatars });
  return true;
}

/**
 * Merge device-keyed roster maps (`deviceHex → displayName` /
 * `deviceHex → accountId`) onto the row. These are what let a group message's
 * bubble AND the chat-list "Alice: hi" preview resolve a sender's identity when
 * the sender is a peer's device we've never received a `ping.profile` from — the
 * authoritative source is `GET /v1/devices/{account}` (via the hydration
 * backfill), not a best-effort broadcast. Union-merge: only writes non-empty
 * values and never blanks an existing entry. Returns true if the row changed.
 */
export async function mergeMemberDeviceMaps(
  id: string,
  maps: {
    memberDeviceNames?: Record<string, string>;
    memberDeviceAccounts?: Record<string, string>;
  },
): Promise<boolean> {
  const conv = await db.conversations.get(id);
  if (conv === undefined) return false;
  const names = { ...(conv.memberDeviceNames ?? {}) };
  const accounts = { ...(conv.memberDeviceAccounts ?? {}) };
  let changed = false;
  for (const [device, name] of Object.entries(maps.memberDeviceNames ?? {})) {
    if (name.length > 0 && names[device] !== name) {
      names[device] = name;
      changed = true;
    }
  }
  for (const [device, account] of Object.entries(maps.memberDeviceAccounts ?? {})) {
    if (account.length > 0 && accounts[device] !== account) {
      accounts[device] = account;
      changed = true;
    }
  }
  if (!changed) return false;
  await db.conversations.update(id, {
    memberDeviceNames: names,
    memberDeviceAccounts: accounts,
  });
  return true;
}

/** Group/conversation avatar (data or remote URL). */
export async function setConversationAvatar(id: string, avatarUri: string): Promise<void> {
  await db.conversations.update(id, { avatarUri });
}

/** Persist the group's STABLE avatar media id. Must be kept in sync with
 *  `avatarUri` on every avatar change: hydration re-resolves the icon from this
 *  id, so a stale id silently reverts a freshly-changed avatar to the old image. */
export async function setConversationAvatarMediaId(
  id: string,
  avatarMediaId: string,
): Promise<void> {
  await db.conversations.update(id, { avatarMediaId });
}

/**
 * Clear a group's OWN avatar (both the stable `avatarMediaId` and its resolved
 * `avatarUri`). Used by hydration when the authoritative MLS meta says a group
 * has NO icon but the row still carries an `avatarMediaId` — which, for a
 * single-peer group briefly mis-seeded as a 1:1, can only be the PEER's avatar
 * media id stamped by the 1:1 hydration branch. Leaving it would re-render the
 * peer's face as the group icon on the next pass (the group→peer-avatar bleed);
 * clearing it lets the row fall back to initials, never a member's picture.
 * Dexie 4 deletes a property set to `undefined` in `update()`.
 */
export async function clearConversationGroupAvatar(id: string): Promise<void> {
  const clear: { avatarMediaId?: string | undefined; avatarUri?: string | undefined } = {
    avatarMediaId: undefined,
    avatarUri: undefined,
  };
  await db.conversations.update(id, clear as Partial<Conversation>);
}

/** Set the conversation's display name. Used to backfill a 1:1's peer name after
 *  a delete+resume re-creates the row with the "New chat" placeholder. */
export async function setConversationName(id: string, name: string): Promise<void> {
  if (name.trim().length === 0) return;
  await db.conversations.update(id, { name: name.trim() });
}

/** Set a 1:1's peer account id. Used to repair a row whose stored
 *  `peerAccountId` is missing or doesn't resolve (e.g. a 32-byte MLS UserId
 *  saved by an older build) from the server's authoritative account roster. */
export async function setConversationPeerAccountId(
  id: string,
  peerAccountId: string,
): Promise<void> {
  if (peerAccountId.trim().length === 0) return;
  await db.conversations.update(id, { peerAccountId: peerAccountId.trim() });
}

/**
 * HEAL a row the MLS meta says is a GROUP but that is stored as a 1:1 (a
 * single-peer group whose Welcome landed before its meta was readable, so it was
 * seeded as `kind:'direct'` with the sole other member as `peerAccountId`). Marks
 * it a durable group (`kind:'group'` + `explicitGroup`) and clears the stray
 * `peerAccountId` so member-profile hydration can never stamp that peer's avatar
 * as the "group avatar" (the group→peer-avatar bleed). Idempotent no-op if the
 * row is already a group. Optionally seeds the group name/avatarMediaId from meta.
 */
export async function markConversationAsGroup(
  id: string,
  meta?: { name?: string; avatarMediaId?: string },
): Promise<void> {
  const row = await db.conversations.get(id);
  if (row === undefined) return;
  if (row.kind === 'group' && (row as { explicitGroup?: boolean }).explicitGroup === true) {
    return;
  }
  const patch: Partial<Conversation> = { kind: 'group', explicitGroup: true };
  // A group must never retain a 1:1 peer id — that is what re-enters the 1:1
  // avatar path and clobbers the group icon with the peer's picture.
  if (row.peerAccountId !== undefined && row.peerAccountId.length > 0) {
    patch.peerAccountId = '';
  }
  if (meta?.avatarMediaId !== undefined && meta.avatarMediaId.length > 0) {
    patch.avatarMediaId = meta.avatarMediaId;
  } else if (
    meta !== undefined &&
    (meta.avatarMediaId === undefined || meta.avatarMediaId.length === 0) &&
    row.avatarMediaId !== undefined &&
    row.avatarMediaId.length > 0
  ) {
    // The authoritative group meta says this group has NO icon, yet the row
    // carries an `avatarMediaId`. This row is still `kind:'direct'` here, so no
    // group avatar broadcast has landed — that media id can ONLY be the 1:1 peer
    // avatar stamped by the direct-hydration branch. Drop it (and its resolved
    // url) so the healed group falls back to initials instead of the peer's face.
    // Dexie 4 deletes a property set to `undefined` in `update()`.
    (patch as { avatarMediaId?: string | undefined }).avatarMediaId = undefined;
    (patch as { avatarUri?: string | undefined }).avatarUri = undefined;
  }
  if (
    meta?.name !== undefined &&
    meta.name.length > 0 &&
    (row.name === undefined || row.name.trim().length === 0 || isPlaceholderInviteName(row.name))
  ) {
    patch.name = meta.name;
  }
  await db.conversations.update(id, patch);
  log.info({ event: 'conversation.healedToGroup', conversationIdPrefix: id.slice(0, 8) });
}

/** Set a group's member account-id roster (16-byte directory account ids).
 *  Used to backfill `memberAccountIds` from the server's account roster when a
 *  group was synced without the `com.ping.group_name` broadcast. */
export async function setConversationMemberAccountIds(
  id: string,
  memberAccountIds: readonly string[],
): Promise<void> {
  const cleaned = memberAccountIds.filter((m) => m.length > 0);
  if (cleaned.length === 0) return;
  await db.conversations.update(id, { memberAccountIds: [...cleaned] });
}

/**
 * Collapse duplicate 1:1 rows for the same peer into ONE continuous
 * conversation. Restarting a chat (delete + re-invite, or sign-out-all +
 * re-invite) mints a BRAND-NEW MLS group with a new conversation id, so the
 * peer who did NOT delete gets a SECOND row for the same person.
 *
 * The party who didn't delete must keep their history AND keep receiving — so
 * we KEEP the most-recently-active row (the live MLS group a re-invite routes
 * messages to, the one sends must go to), MIGRATE every other duplicate's
 * messages into it (preserving the non-deleter's history), then soft-delete the
 * now-empty duplicates. The result is one row per peer whose thread continues
 * seamlessly. Returns the surviving id.
 */
/**
 * True when a conversation row carries a GROUP signal — i.e. it is (or is about
 * to become) a group, even if its `kind` still reads 'direct' because the
 * server classifies a single-peer group as a DM and the MLS meta hasn't upgraded
 * it yet. A genuine 1:1 never carries any of these. Used to keep single-peer
 * groups out of 1:1 de-duplication (B).
 */
/**
 * Peer-aware group check for 1:1 DEDUP. Treating ANY `memberAccountIds` as a
 * group wrongly PROTECTED genuine 1:1 duplicates — an incoming 1:1 Welcome
 * populates `memberAccountIds = [peer]` (the "same person shows 3 times" bug).
 * Here a roster is a group signal only when it contains a member OTHER than the
 * peer (a real multi-member group). Single-PEER groups are still protected: they
 * always carry a `ping:meta:v1:` name / owner / roles / group avatar, all of
 * which still block the merge.
 */
// A conversation row must be at least this old before the 1:1 de-dup is allowed
// to soft-delete it. A freshly-inserted row's classification is still settling:
// a single-peer GROUP first materializes as `kind:'direct'` with a
// `peerAccountId` (the server classifies it as a DM) and only upgrades to a group
// — clearing `peerAccountId` — once `persistIfNew` reads the MLS group meta a few
// hundred ms later. De-duping inside that window could merge a live single-peer
// group into a same-peer 1:1 and lose it. Deferring the collapse by a few seconds
// (rows briefly co-exist) is strictly safer than deleting the wrong one; a
// genuine 1:1 re-invite duplicate still collapses on the next list-load pass once
// both rows have settled.
const DEDUP_SETTLE_MS = 15_000;

// A `kind:'direct'` row is a REAL group (must never be merged into a 1:1) only
// when it carries an unambiguous group identity: the canonical single-peer-group
// meta name (`ping:meta:v1:`) OR a roster with a member OTHER than the peer (a
// genuine multi-member group).
//
// NOTE: this is intentionally NARROWER than a "has any group-ish field" check.
// The dedup below only ever sees rows PAST the settle window (DEDUP_SETTLE_MS),
// and a genuine single-peer group upgrades from `kind:'direct'` to `kind:'group'`
// WITHIN that window — so a row that is still `kind:'direct'` long after settle
// is a 1:1. Its `avatarMediaId` / `ownerAccountId` / `memberRoles` are cosmetic
// residue (a 1:1 can carry a resolved peer avatar, and mis-created rows can carry
// stray owner/role fields) and must NOT permanently block collapse — that left
// same-peer 1:1 rows duplicated forever. Worst case if a truly-corrupted
// stuck-as-direct group is collapsed: its messages migrate into the survivor and
// the empty row is soft-deleted (recoverable), never data loss.
function isRealGroup(
  c: {
    memberAccountIds?: string[];
    name?: string;
    explicitGroup?: boolean;
    kind?: 'direct' | 'group';
  },
  peer: string,
): boolean {
  // DURABLE, race-free authority: a row POSITIVELY classified as a group at any
  // point carries `explicitGroup:true` (or `kind:'group'`) and can NEVER be
  // collapsed into a same-peer 1:1 — this is what keeps a single-peer group and
  // the 1:1 DM with the same person as two distinct chats. Unlike the cosmetic
  // fields (avatarMediaId/ownerAccountId/memberRoles) this is only ever set from
  // real group evidence, so it can't wrongly protect a genuine 1:1 duplicate.
  if (c.explicitGroup === true || c.kind === 'group') return true;
  if (typeof c.name === 'string' && c.name.startsWith('ping:meta:v1:')) return true;
  if (
    c.memberAccountIds !== undefined &&
    c.memberAccountIds.some((m) => m.trim().length > 0 && m !== peer)
  ) {
    return true;
  }
  return false;
}

export async function dedupeDirectConversationsByPeer(
  peerAccountId: string,
): Promise<string | undefined> {
  const peer = peerAccountId.trim();
  if (peer.length === 0) return undefined;
  const now = Date.now();
  const dups = (await db.conversations.toArray()).filter(
    (c) =>
      c.kind === 'direct' &&
      c.peerAccountId === peer &&
      !c.deletedAt &&
      // GROUP-SAFETY: never merge a genuine group into a 1:1. Past the settle
      // window (below), the only rows that must stay protected are the canonical
      // single-peer-group meta (`ping:meta:v1:` name) and real multi-member
      // groups — see `isRealGroup`. Custom-named / avatar'd / owned same-peer
      // 1:1 rows now collapse instead of duplicating forever.
      !isRealGroup(c, peer) &&
      // SETTLE-SAFETY: skip rows still inside the classification window — a
      // single-peer group looks exactly like a 1:1 until it upgrades. See
      // DEDUP_SETTLE_MS.
      now - (c.createdAt ?? 0) >= DEDUP_SETTLE_MS,
  );
  return collapseDuplicateRows(dups);
}

/**
 * Collapse a set of duplicate 1:1 rows into ONE. Keeps the NEWEST MLS group
 * (largest createdAt) — a delete+re-invite / per-call re-create mints a
 * brand-new group, and that newest group is the only LIVE one (where future
 * traffic routes). Migrates every other duplicate's messages into the survivor
 * so history continues seamlessly, then soft-deletes the empties (recoverable).
 * Returns the surviving id.
 */
async function collapseDuplicateRows(
  dups: Array<{ id: string; createdAt?: number; lastMessageAt?: number }>,
  preferId?: string,
): Promise<string | undefined> {
  if (dups.length <= 1) return dups[0]?.id;
  const now = Date.now();
  const createdAt = (c: (typeof dups)[number]): number => c.createdAt ?? 0;
  const lastActivity = (c: (typeof dups)[number]): number => c.lastMessageAt ?? 0;
  const sorted = [...dups].sort(
    (a, b) => createdAt(b) - createdAt(a) || lastActivity(b) - lastActivity(a),
  );
  // Keep the PREFERRED survivor when given (e.g. the row that carries the
  // resolved peer identity), else the newest MLS group. All others' messages
  // migrate into the survivor.
  const preferred = preferId !== undefined ? dups.find((d) => d.id === preferId) : undefined;
  const keep = preferred ?? sorted[0];
  const supersede = sorted.filter((c) => c.id !== keep?.id);
  if (keep === undefined) return undefined;
  for (const c of supersede) {
    // Migrate this duplicate's messages into the survivor so history continues
    // instead of being replaced by an empty new chat. Re-key by conversationId;
    // a content-hash collision (already in the survivor) → drop the dup copy.
    const msgs = await db.messages.where('conversationId').equals(c.id).toArray();
    for (const m of msgs) {
      try {
        await db.messages.update(m.id, { conversationId: keep.id });
      } catch {
        await db.messages.delete(m.id).catch(() => undefined);
      }
    }
    await db.conversations.update(c.id, { deletedAt: now });
  }
  return keep.id;
}

/** Placeholder names a 1:1 carries before its peer profile resolves. */
function isPlaceholderInviteName(name: string): boolean {
  const n = name.trim().toLowerCase();
  return n.length === 0 || n === 'new chat' || n === 'group chat';
}

/**
 * Collapse joined-via-invite 1:1 rows that NEVER resolved a `peerAccountId`,
 * keyed by their resolved display name. These are the ghost rows the invite /
 * per-call flows leave behind (a per-call MLS group whose Welcome beat its
 * suppression, or a re-invite that minted a fresh group) — they have no stable
 * peer key, so the peer-keyed dedup can never touch them and they accumulate
 * ("same person 5×"). The display NAME is the only available identity here.
 *
 * Guards keep this safe: only `direct` + `joinedViaInvite` rows WITHOUT a
 * peerAccountId, with a real (non-placeholder) name, past the settle window, and
 * never a real group. Message migration + soft-delete make a wrong merge (two
 * unresolved people sharing a name) recoverable rather than destructive.
 */
export async function dedupeInviteConversationsByName(): Promise<void> {
  const rows = await db.conversations.toArray();
  const now = Date.now();
  // Bucket every SETTLED, non-group 1:1 with a real (non-placeholder) name by
  // that name, tracking peer-keyed rows (resolved 1:1s) and peerless ghosts
  // separately. This is what lets a row WITH a resolved peer and a row WITHOUT
  // one — for the SAME person — finally collapse: the peer-keyed pass only sees
  // the former, the ghost-only pass only saw the latter, so a duplicate 1:1
  // survived forever. (INVARIANT: never more than one 1:1 per peer.)
  const byName = new Map<
    string,
    {
      peerRows: Array<{ id: string; createdAt?: number; lastMessageAt?: number; peerAccountId?: string }>;
      ghosts: Array<{ id: string; createdAt?: number; lastMessageAt?: number }>;
    }
  >();
  for (const c of rows) {
    if (c.kind !== 'direct' || c.deletedAt) continue;
    const peer = (c.peerAccountId ?? '').trim();
    if (isRealGroup(c, peer)) continue;
    const name = (c.name ?? '').trim();
    if (isPlaceholderInviteName(name)) continue;
    if (now - (c.createdAt ?? 0) < DEDUP_SETTLE_MS) continue;
    const key = name.toLowerCase();
    const entry = byName.get(key) ?? { peerRows: [], ghosts: [] };
    if (peer.length > 0) entry.peerRows.push(c);
    else if (c.joinedViaInvite === true) entry.ghosts.push(c);
    byName.set(key, entry);
  }
  for (const { peerRows, ghosts } of byName.values()) {
    // Distinct people can share a display name — only fold ghosts in when the
    // resolved peer is UNAMBIGUOUS (exactly one peer row for that name). When two
    // peer rows share a name they are (almost certainly) different accounts, so
    // the peer-keyed pass owns them and we leave the ghosts for a later pass once
    // one resolves its own peer.
    const uniquePeers = new Set(peerRows.map((p) => (p.peerAccountId ?? '').trim()));
    if (peerRows.length >= 1 && uniquePeers.size === 1 && ghosts.length > 0) {
      // Fold the peerless ghost(s) INTO the resolved peer row (keep it as the
      // survivor so the peer identity + live group are preserved; ghost history
      // migrates in).
      const survivor = peerRows[0];
      if (survivor !== undefined) {
        await collapseDuplicateRows([...peerRows, ...ghosts], survivor.id).catch(() => undefined);
      }
    } else if (peerRows.length === 0 && ghosts.length > 1) {
      // No resolved peer for this name yet → collapse the ghosts among themselves.
      await collapseDuplicateRows(ghosts).catch(() => undefined);
    }
  }
}

/**
 * Collapse ALL duplicate 1:1 conversations in one pass — one row per peer, like
 * WhatsApp/Slack. Groups every non-deleted, non-group `direct` row by
 * `peerAccountId` and runs the per-peer dedup for any peer with more than one
 * row. Idempotent + cheap (the local conversation cache is small). Run on
 * list-load so ghost "No messages yet" re-invite rows can never accumulate,
 * independent of per-conversation hydration timing.
 */
export async function dedupeAllDirectConversations(): Promise<void> {
  const rows = await db.conversations.toArray();
  const now = Date.now();
  const peers = new Set<string>();
  const counts = new Map<string, number>();
  for (const c of rows) {
    if (c.kind !== 'direct' || c.deletedAt) continue;
    const peer = c.peerAccountId?.trim();
    if (peer === undefined || peer.length === 0) continue;
    if (isRealGroup(c, peer)) continue;
    // Match the per-peer settle guard so a not-yet-classified row (a single-peer
    // group still looking like a 1:1) is never counted as a duplicate.
    if (now - (c.createdAt ?? 0) < DEDUP_SETTLE_MS) continue;
    counts.set(peer, (counts.get(peer) ?? 0) + 1);
    peers.add(peer);
  }
  for (const peer of peers) {
    if ((counts.get(peer) ?? 0) > 1) {
      await dedupeDirectConversationsByPeer(peer).catch(() => undefined);
    }
  }
  // Second pass: collapse joined-via-invite ghosts that never got a peer id
  // (leaked per-call groups, re-invite duplicates) by display name — the
  // peer-keyed pass above can't see them (no peerAccountId).
  await dedupeInviteConversationsByName().catch(() => undefined);
}

/** Delete messages whose `expiresAt` has passed across ALL conversations
 *  (disappearing-messages sweep). Returns the number deleted. */
export async function sweepExpiredMessages(nowMs: number): Promise<number> {
  // `expiresAt` is non-indexed → Dexie `.filter` full-scans the (bounded local)
  // message cache; cheap enough for a periodic sweep.
  const toDelete = await db.messages
    .filter((m) => m.expiresAt !== undefined && m.expiresAt <= nowMs)
    .toArray();
  if (toDelete.length === 0) return 0;
  await db.messages.bulkDelete(toDelete.map((m) => m.id));
  return toDelete.length;
}

/**
 * Clear the LOCAL message cache for a conversation (Chat Info → Settings →
 * "Clear chat"). Removes this device's stored messages + receipts and resets
 * the conversation's preview/unread; the conversation row itself stays so the
 * thread remains and new messages still arrive. Local-only by design: E2EE
 * means we cannot delete messages from the peer's device — this clears the
 * caller's copy, matching the WhatsApp/Signal "clear chat" semantics.
 */
export async function clearConversationMessages(id: string): Promise<void> {
  await db.transaction('rw', db.messages, db.conversations, async () => {
    await db.messages.where('conversationId').equals(id).delete();
    // `modify` lets us DELETE the preview/timestamp keys (an `update({ x:
    // undefined })` is rejected under exactOptionalPropertyTypes).
    await db.conversations
      .where('id')
      .equals(id)
      .modify((c) => {
        delete c.lastMessagePreview;
        delete c.lastMessageAt;
      });
  });
}
